import React from "react";
import classNames from "classnames";
import { ColorPalette } from "../../types/background";
import {
  getBackgroundColorClass,
  getFontColorClass,
} from "../../utils/getBackgroundColor";
import { SectionHeading } from "../SectionHeading/SectionHeading";

interface FullImageBlockProps {
  backgroundImage: string;
  title: string;
  subTitle: string;
  actions: React.ReactNode[];
  videoUrl?: string;
  backgroundColor?: ColorPalette;
  fontColor?: ColorPalette;
  type?: "hero" | "standard";
  fullHeight?: boolean;
  imageCaption?: string;
}

export const FullImageBlock = ({
  backgroundImage,
  title,
  subTitle,
  actions,
  videoUrl,
  backgroundColor,
  fontColor,
  type = "standard",
  fullHeight,
  imageCaption,
}: FullImageBlockProps) => {
  const [imageHeight, setImageHeight] = React.useState<number | null>(null);
  const [imageRatio, setImageRatio] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (backgroundImage) {
      const img = new Image();
      img.src = backgroundImage;
      img.onload = () => {
        const ratio = img.width / img.height;
        setImageHeight(img.height);
        setImageRatio(ratio);
      };
    }
  }, [backgroundImage]);

  const bgClass = getBackgroundColorClass(backgroundColor);
  const fontClass = getFontColorClass(fontColor);
  const heading =
    type === "hero" ? (
      <h1 className={classNames("text-4xl md:text-5xl leading-tight font-medium", fontClass)}>{title}</h1>
    ) : (
      <h2 className={classNames("text-4xl md:text-5xl font-medium", fontClass)}>{title}</h2>
    );
  const heightClass = fullHeight ? "" : (type === "hero" ? "min-h-[640px]" : "min-h-[570px]");
  const headingContainerClasses =
    type === "standard" ? ["pt-16", "self-center", "text-center"] : [];
  const actionsContainerClasses =
    type === "standard" ? ["justify-center", "self-center"] : [];

  return (
    <section
      className={classNames(
        "bg-cover bg-center w-full relative",
        imageCaption ? "md:w-auto md:mt-[64px] md:mb-[64px] md:ml-[128px] md:mr-[128px]" : "m-0",
        heightClass,
        bgClass,
      )}
      style={{
        height: imageCaption && imageHeight ? `${imageHeight}px` : "auto",
        maxHeight: imageHeight ? `${imageHeight}px` : "auto",
        aspectRatio: !imageCaption && imageRatio ? `${imageRatio}` : "auto",
        backgroundRepeat: "no-repeat",
        backgroundSize: fullHeight ? "contain" : "cover",
        backgroundImage: `url(${backgroundImage})`
      }}
    >
      {videoUrl ? (
        <video
          className="object-cover absolute w-full h-full"
          autoPlay
          muted
          loop
          preload="auto"
          id="myVideo"
          poster="../../assets/bgimage.webp"
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      ) : (
        <></>
      )}
      <div
        className={classNames(
          "container py-16 text-left flex flex-col relative",
          fontClass
        )}
      >
        {type === "hero" ? (
          <p className={classNames("pt-16", fontClass)}>{subTitle}</p>
        ) : (
          <SectionHeading colorVariant="white" title={subTitle} />
        )}


        {heading && (
          <div
            className={classNames(
            "w-10/12 md:w-9/12 mb-12",
            ...headingContainerClasses
          )}
        >
            {heading}
          </div>
        )}
        
        {actions && (
          <div
            className={classNames(
              "pb-16 flex space-y-4 flex-col md:flex-row md:space-x-4 md:space-y-0 w-1/2 md:w-8/12",
            ...actionsContainerClasses
          )}
          >
            {actions}
          </div>
        )}
      </div>
      {imageCaption && (
          <div className="hidden md:block absolute" style={{ 
            top: fullHeight ? "570px" : `${imageHeight}px`,
            left: "0"
          }}>
            <p className="text-primary text-base"><b>Foto:</b> {imageCaption}</p>
          </div>
        )}
    </section>
  );
};
